import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Brand } from "@/components/agent/AgentLayout";
import { resetFlight } from "@/components/applicant/FlightPath";
import { AgentDemo } from "@/components/landing/AgentDemo";
import { TenantDemo } from "@/components/landing/TenantDemo";
import { Features } from "@/components/landing/Features";
import { PlanCards } from "@/components/shared/PlanCards";
import { STILL } from "@/components/landing/script";
import { prefersReducedMotion, useLoop } from "@/components/landing/stage";
import { agentDestination, linkProps } from "@/components/landing/destinations";
import { cn } from "@/lib/utils";

/** A link that is an in-app route, or an address outside the app (see destinations.js). */
const Go = ({ to, ...rest }) => {
  const props = linkProps(to);
  return props.to ? <Link {...props} {...rest} /> : <a {...props} {...rest} />;
};

const CHOICES = {
  agent: "bg-foreground text-background shadow-[0_8px_20px_-8px_rgb(0_0_0/0.45)] hover:bg-foreground/90",
  tenant: "bg-brand text-brand-foreground shadow-[0_8px_20px_-8px_hsl(var(--brand-strong)/0.6)] hover:bg-brand-strong",
};

const Choice = ({ to, kind, testid, className, children }) => (
  <Go to={to} data-testid={testid}
    className={cn("group inline-flex h-12 w-full max-w-[300px] items-center justify-center gap-2 justify-self-center rounded-full px-6 text-[15px] font-semibold",
      "outline-none transition-[background-color,transform] duration-200 active:scale-[0.98] focus-visible:ring-[3px] focus-visible:ring-ring/60", CHOICES[kind], className)}>
    {children} <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" strokeWidth={2.4} />
  </Go>
);

const SECTIONS = [["product", "Product"], ["fraud-checks", "Fraud checks"], ["fair", "Fair by design"], ["tenants", "For tenants"], ["pricing", "Pricing"]];

const PLAN_CTA = {
  payg: ["Sign up free", "border bg-background hover:bg-muted"],
  starter: ["Choose Starter", "bg-brand text-brand-foreground hover:bg-brand-strong"],
  pro: ["Choose Pro", "bg-foreground text-background hover:bg-foreground/90"],
};

const Pricing = ({ agentTo }) => (
  <section id="pricing" className="scroll-mt-28 pt-32 md:pt-48" data-testid="landing-pricing">
    <div className="mx-auto max-w-2xl text-center">
      <h2 className="text-balance text-[34px] font-semibold leading-[1.05] tracking-tight md:text-[56px]">Pricing that grows with your lettings.</h2>
      <p className="mx-auto mt-5 max-w-md text-base leading-relaxed text-muted-foreground md:text-lg">
        Start with no monthly fee, then move to a plan as you let more each month.
      </p>
    </div>
    <div className="mt-14 md:mt-20">
      <PlanCards large testid="landing-plans" action={(plan) => {
        const [label, cls] = PLAN_CTA[plan.id];
        return (
          <Go to={agentTo} data-testid={`landing-plan-cta-${plan.id}`}
            className={cn("inline-flex h-11 w-full items-center justify-center rounded-full px-5 text-sm font-semibold outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/60", cls)}>
            {label}
          </Go>
        );
      }} />
    </div>
  </section>
);

const jumpTo = (e, id) => {
  const el = document.getElementById(id);
  if (!el) return;
  e.preventDefault();
  el.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth" });
  window.history.replaceState(null, "", `#${id}`);
};

const pill = "inline-flex h-9 items-center rounded-full px-4 text-sm font-medium transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/60";

/** Stays at the top while scrolling, and gains a hairline once the page has moved under it. */
const TopBar = ({ agentTo }) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header className={cn("sticky top-0 z-50 border-b backdrop-blur-md transition-colors duration-200",
      scrolled ? "border-border bg-background/80" : "border-transparent bg-background/0")} data-testid="landing-topbar">
      <div className="mx-auto grid h-16 max-w-6xl grid-cols-[1fr_auto_1fr] items-center gap-6 px-4 md:px-8">
        <a href="#top" onClick={(e) => jumpTo(e, "top")} aria-label="Landin, back to top" className="justify-self-start"><Brand /></a>
        <nav className="hidden items-center gap-1 md:flex" aria-label="Sections">
          {SECTIONS.map(([id, label]) => (
            <a key={id} href={`#${id}`} onClick={(e) => jumpTo(e, id)} data-testid={`topbar-link-${id}`}
              className={cn(pill, "px-3 text-foreground/75 hover:text-foreground")}>{label}</a>
          ))}
        </nav>
        <Go to={agentTo} data-testid="topbar-sign-in" className={cn(pill, "col-start-3 justify-self-end bg-foreground text-background hover:bg-foreground/85")}>Sign in</Go>
      </div>
    </header>
  );
};

/** The public front page: an agent's dashboard on the left, a tenant's phone on the right, a way in for each, then what Landin does. */
export default function Landing() {
  const still = useMemo(() => (prefersReducedMotion() ? STILL : null), []);
  const t = useLoop(still);
  const agentTo = agentDestination();
  // The demo's plane must not carry over into a real application opened from here.
  useEffect(() => () => resetFlight(), []);

  return (
    <div className="relative isolate min-h-screen overflow-x-clip bg-background text-sm text-foreground" id="top" data-testid="landing-page">
      <TopBar agentTo={agentTo} />

      <main className="mx-auto max-w-6xl px-4 pt-6 md:px-8 md:pt-8">
        <div className="mx-auto max-w-3xl text-center">
          <h1 className="text-[34px] font-semibold leading-[1.08] tracking-tight md:text-[48px]">Land the deal.<br className="md:hidden" /> Land the home.</h1>
          <p className="mx-auto mt-3 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
            Same-day tenant referencing. Letting agents add a property and share one link. Tenants apply from their phone, and every document is checked in seconds.
          </p>
        </div>

        <p className="sr-only">
          Animated example: on the left, a letting agent adds two properties to their Landin dashboard. On the right, a tenant applies on their phone,
          uploads their ID and payslips, connects their bank, and sends the application, which then appears on the agent's dashboard.
        </p>
        {/* On wider screens the phone shrinks with the window height, so the demos and both buttons fit on one screen. */}
        <div className="mt-8 grid gap-x-10 gap-y-5 md:grid-cols-[minmax(0,1fr)_auto] md:items-center lg:gap-x-14">
          <div className="order-3 mt-6 w-full md:order-none md:mt-0 md:max-w-[calc((100vh_-_350px)*1.55)] md:justify-self-center" aria-hidden="true" inert>
            <AgentDemo t={t} />
          </div>
          <div className="order-4 mt-4 md:order-none md:mt-0 md:w-[clamp(200px,calc((100vh_-_350px)/2.09),300px)]" aria-hidden="true" inert>
            <TenantDemo t={t} />
          </div>
          <Choice to={agentTo} kind="agent" testid="landing-agent-button" className="order-1 md:order-none md:mt-1">I'm a letting agent</Choice>
          <Choice to="/tenant" kind="tenant" testid="landing-tenant-button" className="order-2 md:order-none md:mt-1">I'm a tenant</Choice>
        </div>

        <hr className="mb-24 mt-24 md:mb-36 md:mt-32" />
        <Features agentTo={agentTo} />
        <Pricing agentTo={agentTo} />

        <section className="py-32 text-center md:py-48" data-testid="landing-closing">
          <h2 className="text-balance text-[34px] font-semibold leading-[1.05] tracking-tight md:text-[56px]">Reference your next tenant today.</h2>
          <p className="mx-auto mt-5 max-w-md text-base leading-relaxed text-muted-foreground md:text-lg">
            Add a property, share the link, and have a decision the same day.
          </p>
          <div className="mx-auto mt-10 grid max-w-[620px] gap-3 sm:grid-cols-2">
            <Choice to={agentTo} kind="agent" testid="landing-closing-agent-button">I'm a letting agent</Choice>
            <Choice to="/tenant" kind="tenant" testid="landing-closing-tenant-button">I'm a tenant</Choice>
          </div>
        </section>
      </main>

      <footer className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 border-t px-4 py-8 text-xs text-muted-foreground sm:flex-row md:px-8">
        <Brand />
        <span>Same-day tenant referencing for letting agents</span>
      </footer>
    </div>
  );
}
